import { client } from '~publish/legacy/apollo-client'
import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'

import type { HashtagGroup } from './types'

/**
 * Account-actor mutation that replaces the legacy `createHashtagGroup` RPC /
 * `POST /1/snippet_groups/create.json` for Publish Hashtag Manager.
 * Backend ships as Snippet GraphQL (CHAN-12108), not HashtagGroup naming.
 */
export const CreateSnippet = graphql(/* GraphQL */ `
  mutation CreateSnippet($input: CreateSnippetInput!) {
    createSnippet(input: $input) {
      __typename
      ... on CreateSnippetSuccess {
        snippet {
          id
          name
          text
        }
      }
      ... on VoidMutationError {
        message
      }
    }
  }
`)

type CreateSnippetPayload = NonNullable<
  ResultOf<typeof CreateSnippet>['createSnippet']
>

export function mapSnippetToHashtagGroup(
  snippet: Extract<
    CreateSnippetPayload,
    { __typename: 'CreateSnippetSuccess' }
  >['snippet'],
): HashtagGroup {
  return {
    _id: snippet.id,
    id: snippet.id,
    name: snippet.name,
    text: snippet.text,
  }
}

export async function createHashtagGroupViaGraphQL(args: {
  organizationId: string
  name: string
  text: string
}): Promise<HashtagGroup> {
  const { data } = await client.mutate({
    mutation: CreateSnippet,
    variables: {
      input: {
        organizationId: args.organizationId,
        name: args.name,
        text: args.text,
      },
    },
  })

  const payload = data?.createSnippet
  if (!payload) {
    throw new Error('CreateSnippet returned no payload')
  }
  if (payload.__typename === 'VoidMutationError') {
    throw new Error(payload.message)
  }
  if (payload.__typename !== 'CreateSnippetSuccess') {
    throw new Error('CreateSnippet returned an unexpected payload')
  }

  return mapSnippetToHashtagGroup(payload.snippet)
}
